import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import { categories, heroSlides, featuredProducts, trendingArticles, promotions } from '@/mocks/home';

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [formUrl, setFormUrl] = useState('');
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [subscribeMsg, setSubscribeMsg] = useState('');
  const NEWSLETTER_FORM_URL = 'https://readdy.ai/api/form/d9sm5o5lbrt1qcgaj61g';

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);
    const honeypot = formData.get('company_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setSubscribeStatus('success');
      setSubscribeMsg('感谢订阅！');
      setSubscribeEmail('');
      return;
    }

    formData.delete('company_alt');

    try {
      const response = await fetch(NEWSLETTER_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setSubscribeStatus('success');
        setSubscribeMsg('感谢订阅！最新穿搭资讯将发送至您的邮箱。');
        setSubscribeEmail('');
      } else {
        const errMsg = parsed?.meta?.message || parsed?.message || '订阅失败，请稍后再试';
        setSubscribeStatus('error');
        setSubscribeMsg(errMsg);
      }
    } catch {
      setSubscribeStatus('error');
      setSubscribeMsg('网络错误，请稍后再试');
    }
  };

  const siteSchema = {
    '@type': 'WebSite',
    name: '蘑菇网',
    url: 'https://www.moguwang.com',
    description: '蘑菇网是专注时尚女装的购物平台，汇聚海量潮流穿搭、新款女装、美妆配饰。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.moguwang.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@type': 'Organization',
    name: '蘑菇网',
    url: 'https://www.moguwang.com',
    description: '专注时尚女装的购物平台',
    sameAs: [
      'https://weibo.com/moguwang',
      'https://www.xiaohongshu.com/vendor/moguwang',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-6688',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <>
      <SEOHead
        title="蘑菇网 - 时尚女装购物首选平台 | 蘑菇网官网入口"
        description="蘑菇网是专注时尚女装的购物平台，汇聚海量潮流穿搭、新款女装、美妆配饰，让每位女性轻松找到心仪单品。蘑菇网为你提供个性化穿搭推荐、超值折扣与安心购物体验。"
        keywords="蘑菇网,蘑菇网女装,蘑菇网官网入口,蘑菇网穿搭推荐,蘑菇网折扣优惠,蘑菇网新款上市,时尚女装购物平台"
        canonicalUrl="https://www.moguwang.com"
        ogImage="https://readdy.ai/api/search-image?query=Elegant%20fashion%20women%20clothing%20display%20with%20warm%20neutral%20tones%2C%20minimalist%20aesthetic%2C%20soft%20natural%20lighting%2C%20clean%20background%2C%20editorial%20quality&width=1200&height=630&seq=og-home&orientation=landscape"
        schema={[siteSchema, orgSchema]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative h-[420px] md:h-[600px] w-full overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Elegant%20fashion%20women%20clothing%20editorial%20photography%20with%20soft%20warm%20lighting%2C%20flowing%20fabric%20in%20neutral%20beige%20and%20cream%20tones%2C%20minimalist%20artistic%20composition%2C%20dreamy%20atmosphere%2C%20fashion%20magazine%20style%2C%20natural%20textures%2C%20aesthetic%20background&width=1600&height=900&seq=hero-home-01&orientation=landscape"
              alt="蘑菇网时尚女装"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/30"></div>
          </div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4 md:px-6 text-center">
            <h1 className="font-heading text-2xl md:text-4xl font-semibold text-background-50 mb-4 max-w-2xl">
              {heroSlides[currentSlide].title}
            </h1>
            <p className="text-sm md:text-base text-background-50/80 mb-8 max-w-xl">
              {heroSlides[currentSlide].subtitle}
            </p>
            <Link
              to={heroSlides[currentSlide].link}
              className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              {heroSlides[currentSlide].cta}
            </Link>
            <div className="flex items-center gap-2 mt-8">
              {heroSlides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentSlide(i)}
                  className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                    i === currentSlide ? 'bg-background-50 w-6' : 'bg-background-50/40'
                  }`}
                  aria-label={`切换到第${i + 1}张`}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Promotions Bar */}
        <section className="w-full bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {promotions.map((promo) => (
                <div key={promo.title} className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className={`${promo.icon} text-lg`}></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{promo.title}</p>
                    <p className="text-xs text-foreground-500">{promo.subtitle}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-3">探索品类</h2>
              <p className="text-sm md:text-base text-foreground-500">发现属于你的时尚风格</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex flex-col items-center gap-3 p-6 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer group"
                >
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-background-50 group-hover:bg-primary-100 transition-colors">
                    <i className={`${cat.icon} text-2xl text-foreground-700 group-hover:text-primary-600`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 whitespace-nowrap">{cat.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Products */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-10 gap-4">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-2">人气推荐</h2>
                <p className="text-sm text-foreground-500">精选当季热门单品</p>
              </div>
              <Link to="/category/dresses" className="text-sm font-medium text-primary-500 hover:text-primary-600 whitespace-nowrap cursor-pointer">
                查看全部 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5"
              data-product-shop
            >
              {featuredProducts.map((product) => (
                <Link
                  key={product.id}
                  to={`/category/${product.category}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-3">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <span className="absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-medium bg-accent-500 text-background-50">
                      {product.tag}
                    </span>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors mb-1 line-clamp-2">
                    {product.name}
                  </h3>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-primary-600">¥{product.price}</span>
                    <span className="text-xs text-foreground-400 line-through">¥{product.originalPrice}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Promo Banner */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="relative rounded-lg overflow-hidden h-[200px] md:h-[280px]">
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20fashion%20sale%20banner%20with%20soft%20warm%20beige%20and%20cream%20gradient%20background%2C%20flowing%20fabric%20textures%2C%20luxurious%20yet%20simple%20composition%2C%20editorial%20quality%2C%20clean%20aesthetic&width=1400&height=560&seq=promo-banner-01&orientation=landscape"
                alt="限时特惠"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent"></div>
              <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-12">
                <span className="text-xs md:text-sm font-medium text-accent-300 mb-2">限时活动</span>
                <h3 className="font-heading text-xl md:text-2xl font-semibold text-background-50 mb-3">秋季新品 · 全场低至3折</h3>
                <Link
                  to="/category/dresses"
                  className="px-6 py-2.5 rounded-full bg-background-50 text-foreground-900 font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-background-100 transition-colors inline-block w-fit"
                >
                  立即抢购
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Articles */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-10 gap-4">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-2">穿搭灵感</h2>
                <p className="text-sm text-foreground-500">最新时尚资讯与穿搭指南</p>
              </div>
              <Link to="/articles" className="text-sm font-medium text-primary-500 hover:text-primary-600 whitespace-nowrap cursor-pointer">
                查看全部 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {trendingArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group cursor-pointer rounded-lg overflow-hidden bg-background-50 hover:bg-background-100 transition-colors"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4 md:p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs text-accent-600 bg-accent-100 px-2 py-0.5 rounded">{article.category}</span>
                      <span className="text-xs text-foreground-400">{article.date}</span>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors mb-2 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2">{article.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-3">订阅蘑菇周刊</h2>
            <p className="text-sm md:text-base text-foreground-500 mb-8">每周获取最新穿搭灵感、专属折扣和新品资讯</p>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-readdy-form>
              <input
                type="text"
                name="company_alt"
                className="honeypot-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
              <input
                type="email"
                name="email"
                value={subscribeEmail}
                onChange={(e) => setSubscribeEmail(e.target.value)}
                placeholder="输入您的邮箱地址"
                required
                className="flex-1 px-4 py-3 rounded-full border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
              />
              <button
                type="submit"
                className="px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
              >
                立即订阅
              </button>
            </form>
            {subscribeStatus !== 'idle' && (
              <p className={`mt-3 text-sm ${subscribeStatus === 'success' ? 'text-primary-600' : 'text-red-500'}`}>
                {subscribeMsg}
              </p>
            )}
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}